import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import { useContext } from '@nuxtjs/composition-api';
import { useShare } from '@vueuse/core';
import { useCopyToClipboard } from '~/composables/useCopyToClipboard';
import { useShareLinks } from '~/composables/useShareLinks';
import { useId } from '~/composables/useUniqueId';
import { InputSize } from '~/types/vuetify';

type ShareLinkOption = 'copy' | 'facebook' | 'twitter' | 'share';

interface IShareButton {
  icon: string;
  key: ShareLinkOption;
  label: string;
  props: Record<string, unknown>;
  on?: Record<string, unknown>;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ShareLinks',
  props: {
    fab: { type: Boolean, default: true },
    icon: { type: Boolean, default: false },
    buttonColor: null,
    buttonClass: null,
    buttonSize: null,
    buttonTextColor: null,
    iconColor: null,
    iconSize: null,
    heading: null,
    headingClass: null,
    labels: null,
    listClass: null,
    urls: null
  },
  setup(__props: any) {

const props = __props as { fab: boolean, icon: boolean, buttonColor?: string, buttonClass?: string, buttonSize?: InputSize, buttonTextColor?: string, iconColor?: string, iconSize?: string | number, heading?: string, headingClass?: string, labels?: Record<ShareLinkOption, string>, listClass?: string, urls?: Partial<Record<'url' | 'twitter' | 'facebook', string>> };



const { i18n } = useContext();
const headingId = useId('heading');
const shareLinks = computed(() => {
  if (props.urls) {
    return props.urls;
  }
  const { url, twitter, facebook } = useShareLinks();
  return { url: url.value, twitter: twitter.value, facebook: facebook.value };
});

const shareOnFacebookUrl = computed(() => {
  if (!shareLinks.value.facebook) {
    return '';
  }
  const url = new URL('https://www.facebook.com/sharer/sharer.php');
  url.searchParams.append('u', shareLinks.value.facebook);
  return url.toString();
});

const shareOnTwitterUrl = computed(() => {
  if (!shareLinks.value.twitter) {
    return '';
  }
  const url = new URL('https://twitter.com/intent/tweet');
  url.searchParams.append('url', shareLinks.value.twitter);
  return url.toString();
});

const { copyToClipboard } = useCopyToClipboard();
const copy = () => {
  if (shareLinks.value.url) {
    copyToClipboard(
      shareLinks.value.url,
      i18n.t('components.uiComponents.shareLinks.link').toString(),
      'bottom',
    );
  }
};

const navShareOptions = computed(() => ({ url: shareLinks.value.url }));
const { share, isSupported: isShareSupported } = useShare(navShareOptions);

const btnProps = computed(() => {
  return {
    color: props.buttonColor,
    class: `share-button rounded-pill font--button ${
      props.buttonTextColor ? props.buttonTextColor + '--text' : ''
    } ${props.buttonClass || ''}`,
    depressed: true,
    fab: props.fab,
    icon: props.icon,
    'x-small': props.buttonSize === 'x-small',
    small: props.buttonSize === 'small',
    medium: props.buttonSize === 'medium',
    large: props.buttonSize === 'large',
    'x-large': props.buttonSize === 'x-large',
  };
});

const headingProps = computed(() => ({
  class: `share-heading ${props.headingClass || ''}`,
  id: headingId,
}));

const listProps = computed(() => ({
  ['aria-labelledby']: headingId,
  class: `share-list ${
    props.fab || props.icon ? 'share-list--icon-only' : ''
  } ${props.listClass || ''}`,
}));

const labels = computed(() => {
  const prefix = 'components.uiComponents.shareLinks.labels';
  return {
    copy: props.labels?.copy || i18n.t(`${prefix}.copy`).toString(),
    facebook: props.labels?.facebook || i18n.t(`${prefix}.facebook`).toString(),
    twitter: props.labels?.twitter || i18n.t(`${prefix}.twitter`).toString(),
    share: props.labels?.share || i18n.t(`${prefix}.share`).toString(),
  };
});

const btns = computed(() => {
  const items: IShareButton[] = [];
  if (shareLinks.value.url) {
    // Copy url button
    items.push({
      key: 'copy',
      icon: 'fas fa-link',
      label: labels.value.copy,
      props: btnProps.value,
      on: {
        click: copy,
      },
    });

    // Navigator share button
    if (isShareSupported.value) {
      items.push({
        key: 'share',
        icon: 'fas fa-arrow-up-from-bracket',
        label: labels.value.share,
        props: btnProps.value,
        on: {
          click: share,
        },
      });
    }
  }

  // Facebook and Twitter buttons
  if (shareOnTwitterUrl.value) {
    items.push({
      key: 'twitter',
      icon: 'fab fa-x-twitter',
      label: labels.value.twitter,
      props: {
        ...btnProps.value,
        nuxt: true,
        target: '_blank',
        href: shareOnTwitterUrl.value,
      },
    });
  }

  if (shareOnFacebookUrl.value) {
    items.push({
      key: 'facebook',
      icon: 'fab fa-facebook',
      label: labels.value.facebook,
      props: {
        ...btnProps.value,
        nuxt: true,
        target: '_blank',
        href: shareOnFacebookUrl.value,
      },
    });
  }

  return items;
});

return { __sfc: true,props, i18n, headingId, shareLinks, shareOnFacebookUrl, shareOnTwitterUrl, copyToClipboard, copy, navShareOptions, share, isShareSupported, btnProps, headingProps, listProps, labels, btns }
}

})